import { useEffect, useState } from 'react';
import { DragDropContext, DropResult, DroppableProvided } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';

import { CreateColumnModal } from './components/CreateColumnModal';
import { KanbanColumn } from './components/KanbanColumn';
import { StrictModeDroppable } from './components/StrictModeDroppable';

import { KanbanCardModal } from '@/apps/Kanban/components/KanbanCardModal';
import { useKanbanStore } from '@/apps/Kanban/stores/useKanbanStore';
import LoadingSpinner from '@/common/components/LoadingSpinner';

export default function Kanban({ widgetId }: { widgetId: string }) {
  const { t } = useTranslation();
  const {
    columns,
    loading,
    fetchBoard,
    addColumn,
    addCard,
    deleteCard,
    deleteColumn,
    reorderCard,
    reorderColumn,
    updateCard,
  } = useKanbanStore();
  const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<{
    id: string;
    content: string;
    description: string;
  } | null>(null);
  const [isCreateColumnModalOpen, setIsCreateColumnModalOpen] = useState(false);

  const handleAddCard = (columnId: string, content: string) => {
    addCard(columnId, content);
    setActiveColumnId(null);
  };

  useEffect(() => {
    fetchBoard(widgetId);
  }, [fetchBoard, widgetId]);

  const handleAddColumn = (title: string) => {
    if (!title.trim()) return;
    addColumn(widgetId, title);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    if (result.type === 'COLUMN') {
      reorderColumn(result);
    } else {
      reorderCard(result);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="min-h-full py-2 px-2">
          <DragDropContext onDragEnd={handleDragEnd}>
            <StrictModeDroppable direction="horizontal" droppableId="board" type="COLUMN">
              {(provided: DroppableProvided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="flex gap-2 h-full relative"
                >
                  {columns.map((column, index) => (
                    <div key={column.id} className="flex-shrink-0">
                      <KanbanColumn
                        activeColumnId={activeColumnId}
                        column={column}
                        index={index}
                        setActiveColumnId={setActiveColumnId}
                        onAddCard={handleAddCard}
                        onCardClick={(card) => setSelectedCard(card)}
                        onDeleteCard={deleteCard}
                        onDeleteColumn={deleteColumn}
                      />
                    </div>
                  ))}
                  {provided.placeholder}
                  <div className="flex-shrink-0 w-80 bg-gray-50 rounded-xl border border-dashed border-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors">
                    <button
                      className="w-full h-full py-4 text-gray-500 hover:text-gray-700"
                      onClick={() => setIsCreateColumnModalOpen(true)}
                    >
                      {t('kanban.addColumn')}
                    </button>
                  </div>
                </div>
              )}
            </StrictModeDroppable>
          </DragDropContext>
        </div>
      </div>
      {loading && <LoadingSpinner />}
      {selectedCard && (
        <KanbanCardModal
          card={selectedCard}
          isOpen={!!selectedCard}
          onClose={() => setSelectedCard(null)}
          onUpdate={updateCard}
        />
      )}
      <CreateColumnModal
        isOpen={isCreateColumnModalOpen}
        onClose={() => setIsCreateColumnModalOpen(false)}
        onSubmit={handleAddColumn}
      />
    </div>
  );
}
